import { Helmet } from "react-helmet-async";

const SITE_URL = "https://disruptiva.online";
const SITE_NAME = "Disruptiva";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg";

export const SEOHead = ({ title, description, path = "/", type = "website", article = null, faqs = null, service = null }) => {
  const fullTitle = title ? `${title}` : `${SITE_NAME} — Comunicación Estratégica`;
  const fullUrl = `${SITE_URL}${path}`;
  const desc = description || "Agencia de comunicación estratégica líder en El Salvador y Centroamérica. Gestión de crisis, reputación corporativa, relaciones públicas y producción de contenido.";

  // Compact ProfessionalService schema (homepage only)
  const orgSchema = path === "/" ? {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#professional-service`,
    "name": "Disruptiva",
    "url": SITE_URL,
    "logo": DEFAULT_IMAGE,
    "description": desc,
    "telephone": "+50364330431",
    "email": "informacion@disruptiva.online",
    "priceRange": "$$",
    "address": { "@type": "PostalAddress", "addressCountry": "SV", "addressLocality": "San Salvador" },
    "geo": { "@type": "GeoCoordinates", "latitude": "13.6929", "longitude": "-89.2182" },
    "areaServed": [
      { "@type": "Country", "name": "El Salvador" },
      { "@type": "Country", "name": "Guatemala" },
      { "@type": "Country", "name": "Honduras" },
      { "@type": "Country", "name": "Costa Rica" },
      { "@type": "Country", "name": "Panamá" }
    ],
    "sameAs": [
      "https://www.instagram.com/agenciadisruptiva_/",
      "https://www.facebook.com/AgenciaDisruptivaComunicacion/",
      "https://www.linkedin.com/company/disruptiva-comunicaci%C3%B3n/"
    ],
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "47", "bestRating": "5" }
  } : null;

  // FAQ schema (homepage only, for AEO)
  const homeFaqSchema = path === "/" ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "¿Qué hace una agencia de comunicación estratégica?", "acceptedAnswer": { "@type": "Answer", "text": "Una agencia de comunicación estratégica protege y amplifica la reputación de organizaciones a través de gestión de crisis, relaciones públicas, media training, social listening y producción audiovisual corporativa." } },
      { "@type": "Question", "name": "¿Cuánto cuesta una agencia de comunicación en El Salvador?", "acceptedAnswer": { "@type": "Answer", "text": "Los costos varían según el servicio. Disruptiva ofrece diagnósticos gratuitos de 7 pasos para evaluar necesidades antes de presentar propuesta. Contacta al +503 6433 0431." } },
      { "@type": "Question", "name": "¿Cómo manejar una crisis de reputación corporativa?", "acceptedAnswer": { "@type": "Answer", "text": "Requiere: War Room 24/7, vocería definida, respuesta coordinada en medios y redes sociales, y plan de reconstrucción post-crisis." } },
      { "@type": "Question", "name": "¿Disruptiva trabaja en toda Centroamérica?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. Operamos desde El Salvador con cobertura en Guatemala, Honduras, Costa Rica, Panamá y Europa." } }
    ]
  } : null;

  // HowTo for diagnostic (homepage only)
  const howToSchema = path === "/" ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Diagnóstico comunicacional para empresas",
    "description": "Evaluación gratuita de 7 pasos para identificar riesgos reputacionales y oportunidades de comunicación estratégica.",
    "totalTime": "PT3M",
    "step": [
      { "@type": "HowToStep", "position": 1, "name": "Identifica tu situación", "text": "Selecciona si enfrentas crisis, prevención, posicionamiento o reinvención." },
      { "@type": "HowToStep", "position": 2, "name": "Define tu sector", "text": "Corporativo, gobierno, ONG o tecnología." },
      { "@type": "HowToStep", "position": 3, "name": "Tamaño de organización", "text": "Startup, mediana, corporación o institución." },
      { "@type": "HowToStep", "position": 4, "name": "Estado comunicacional", "text": "Equipo interno, agencia, freelancers o sin estrategia." },
      { "@type": "HowToStep", "position": 5, "name": "Principal preocupación", "text": "Reputación, visibilidad, comunicación interna o contenido." },
      { "@type": "HowToStep", "position": 6, "name": "Presupuesto", "text": "Listo, evaluando, necesita propuesta o explorando." },
      { "@type": "HowToStep", "position": 7, "name": "Urgencia", "text": "Inmediato, esta semana, este mes o planificando." }
    ]
  } : null;

  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "author": { "@type": "Organization", "name": "Disruptiva" },
    "publisher": { "@type": "Organization", "name": "Disruptiva", "logo": { "@type": "ImageObject", "url": DEFAULT_IMAGE } },
    "datePublished": article.published_date,
    "mainEntityOfPage": { "@type": "WebPage", "@id": fullUrl },
    "articleSection": article.category,
    "inLanguage": "es",
  } : null;

  const faqSchema = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  } : null;

  const breadcrumbSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": fullUrl },
    ]
  } : null;

  const serviceBreadcrumb = service ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Servicios", "item": `${SITE_URL}/#services` },
      { "@type": "ListItem", "position": 3, "name": service.name, "item": fullUrl },
    ]
  } : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:image" content={DEFAULT_IMAGE} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Article meta */}
      {article && <meta property="article:published_time" content={article.published_date} />}
      {article && <meta property="article:section" content={article.category} />}

      {/* JSON-LD Schemas — only render what's relevant per page */}
      {orgSchema && <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>}
      {homeFaqSchema && <script type="application/ld+json">{JSON.stringify(homeFaqSchema)}</script>}
      {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {serviceBreadcrumb && <script type="application/ld+json">{JSON.stringify(serviceBreadcrumb)}</script>}
    </Helmet>
  );
};
